import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAllReportsStore } from '../../../../Storage/allReports/useAllReportsStore'
import {
  PRIORITY_TYPES,
  TABLE_HEADERS,
  REPORT_TYPES,
  STATUS_TYPES,
  DEPARTMENT_TYPES,
} from '../../utils/tableTypes'
import { useAPI } from '../../../../Contexts/APIContext'
import EditableCell from '../EditableCell'
import SelectCell from '../SelectCell'
import successToast from '../../../../Components/Toasts/SuccessToast'
import errorToast from '../../../../Components/Toasts/ErrorToast'
import PropTypes from 'prop-types'
import classes from './ReportsTable.module.css'
import SelectCellColored from '../SelectCellColored'

function ReportRow({
  r,
  idx,
  isLast,
  lastRowRef,
  selectedRowId,
  setSelectedRowId,
  handleStatusChange,
  handleDepartmentChange,
  handlePriorityChange,
  handleEditorNotesChange,
  handleAssignedChange,
}) {
  return (
    <tr
      key={r.f_id}
      ref={isLast ? lastRowRef : null}
      id={isLast ? 'lastRowRef' : null}
      onClick={() => setSelectedRowId(r.f_id)}
      className={selectedRowId === r.f_id ? classes.selectedRow : ''}
    >
      <td className={classes.selectCell}>
        <SelectCell
          value={r.status}
          onChange={newValue => handleStatusChange(r.f_id, newValue)}
          options={STATUS_TYPES}
        />
      </td>
      <td>{r.object || '-'}</td>

      <td>{REPORT_TYPES[r.report_type] || '-'}</td>
      <td>
        {r.report_data?.item_link ? (
          <Link to={r.report_data?.item_link} target="_blank" rel="noreferrer">
            {r.song?.name || r.feels?.song_name || r.artist?.name || 'View'}
          </Link>
        ) : r.feels_f_id ? (
          <Link to={`/feel/${r.feels_f_id}`} target="_blank" rel="noreferrer">
            {r.feels?.song_name || 'View Feel'}
          </Link>
        ) : (
          r.song?.name || r.artist?.name || '-'
        )}
      </td>
      <td>
        <Link
          target="_blank"
          to={`/artist/${r.artist?.artist_name || r.feels?.artist?.artist_name || r.song?.artist?.artist_name}`}
        >
          {r.song?.artist?.name || r.feels?.artist?.name || r.report_data?.artist_display_name || '-'}
        </Link>
      </td>
      <td>
        {r.report_data?.youtube_url ? (
          <Link
            to={r.report_data?.youtube_url}
            target="_blank"
            rel="noreferrer"
          >
            YouTube
          </Link>
        ) : (
          '-'
        )}
      </td>
      <td>{r.report_text || '-'}</td>
      <td
        className={classes.selectCell}
        style={{
          backgroundColor:
            PRIORITY_TYPES.find(
              opt => Number(Object.keys(opt)[0]) === r.priority
            )?.color || 'transparent',
        }}
      >
        <SelectCellColored
          value={r.priority}
          onChange={newValue => handlePriorityChange(r.f_id, newValue)}
          options={PRIORITY_TYPES}
        />
      </td>
      <td>{r.report_user_id || '-'}</td>
      <td>{r.organization || '-'}</td>
      <td>{r.organization_role || '-'}</td>
      <td className={classes.selectCell}>
        <SelectCell
          value={r.department || '-'}
          onChange={newValue => handleDepartmentChange(r.f_id, newValue)}
          options={DEPARTMENT_TYPES}
        />
      </td>
      <EditableCell
        value={r?.assigned || ''}
        onSave={newValue => handleAssignedChange(r.f_id, newValue)}
      />
      <td>
        {r?.report_data?.previous_page ? (
          <Link
            to={r.report_data.previous_page}
            target="_blank"
            rel="noreferrer"
          >
            Previous Page
          </Link>
        ) : (
          '-'
        )}
      </td>
      <td>{r?.report_data?.artist_display_name || '-'}</td>
      <td>{r?.report_data?.new_organization_name || '-'}</td>
      <td>{r?.report_data?.all_rights_carry_forward || '-'}</td>
      <td>{r?.report_data?.label_start_date || '-'}</td>
      <td>{r?.report_data?.previous_label_name || '-'}</td>
      <td>{r?.report_data?.previous_label_start_date || '-'}</td>
      <td>{r?.report_data?.previous_label_end_date || '-'}</td>
      <EditableCell
        value={r?.editor_notes || ''}
        onSave={newValue => handleEditorNotesChange(r.f_id, newValue)}
      />
      <td>{r?.num_reports || '-'}</td>
      <td>
        {r.date_created ? new Date(r.date_created).toLocaleDateString() : '-'}
      </td>
      <td>
        {r.date_updated ? new Date(r.date_updated).toLocaleDateString() : '-'}
      </td>
      <td>{r.f_id || ''}</td>
    </tr>
  )
}

ReportRow.propTypes = {
  r: PropTypes.object.isRequired,
  idx: PropTypes.number.isRequired,
  isLast: PropTypes.bool.isRequired,
  lastRowRef: PropTypes.any,
  selectedRowId: PropTypes.any,
  setSelectedRowId: PropTypes.func.isRequired,
  handleDepartmentChange: PropTypes.func.isRequired,
  handleStatusChange: PropTypes.func.isRequired,
  handlePriorityChange: PropTypes.func.isRequired,
  handleEditorNotesChange: PropTypes.func.isRequired,
  handleAssignedChange: PropTypes.func.isRequired,
}

const ReportsTable = ({ reports, lastRowRef, isError, error }) => {
  // zustand storage
  const filterByObject = useAllReportsStore(state => state.selectedOption.id)
  const filterByStatus = useAllReportsStore(state => state.selectedStatus.id)
  const filterByResolved = useAllReportsStore(state => state.isResolved)

  const queryClient = useQueryClient()
  const { updateReportTableData } = useAPI()

  const [selectedRowId, setSelectedRowId] = useState(null)

  // DEPARTMENT
  const handleDepartmentChange = (reportId, newDepartment) => {
    updateDepartmentMutation.mutate({
      reportID: reportId,
      department: newDepartment,
    })
  }

  const updateDepartmentMutation = useMutation({
    mutationFn: ({ reportID, department }) =>
      updateReportTableData(reportID, null, department),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports', filterByObject, filterByStatus, filterByResolved],
      })
    },
    onError: error => {
      errorToast('Failed to update the table data. ', error)
    },
    onSettled: () => {
      successToast('Successfully updated!')
    },
  })

  // STATUS
  const handleStatusChange = (reportId, newStatus) => {
    updateStatusMutation.mutate({ reportID: reportId, status: newStatus })
  }

  const updateStatusMutation = useMutation({
    mutationFn: ({ reportID, status }) =>
      updateReportTableData(reportID, status),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports', filterByObject, filterByStatus, filterByResolved],
      })
    },
    onError: error => {
      errorToast('Failed to update the table data. ', error)
    },
    onSettled: () => {
      successToast('Successfully updated!')
    },
  })

  // PRIORITY
  const handlePriorityChange = (reportId, newPriority) => {
    updatePriorityMutation.mutate({
      reportID: reportId,
      priority: newPriority,
    })
  }

  const updatePriorityMutation = useMutation({
    mutationFn: ({ reportID, priority }) =>
      updateReportTableData(reportID, null, null, priority),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports', filterByObject, filterByStatus, filterByResolved],
      })
    },
    onError: error => {
      errorToast('Failed to update the table data. ', error)
    },
    onSettled: () => {
      successToast('Successfully updated!')
    },
  })

  //EDITOR NOTES
  const handleEditorNotesChange = (reportId, newValue) => {
    updateEditorNotesMutation.mutate({
      reportID: reportId,
      editor_notes: newValue,
    })
  }

  const updateEditorNotesMutation = useMutation({
    mutationFn: ({ reportID, editor_notes }) =>
      updateReportTableData(reportID, null, null, null, editor_notes),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports', filterByObject, filterByStatus, filterByResolved],
      })
    },
    onError: error => {
      errorToast('Failed to update the table data. ', error)
    },
    onSettled: () => {
      successToast('Successfully updated!')
    },
  })

  //ASSIGNED
  const handleAssignedChange = (reportId, newValue) => {
    updateAssignedMutation.mutate({
      reportID: reportId,
      assigned: newValue,
    })
  }

  const updateAssignedMutation = useMutation({
    mutationFn: ({ reportID, assigned }) =>
      updateReportTableData(reportID, null, null, null, null, assigned),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['reports', filterByObject, filterByStatus, filterByResolved],
      })
    },
    onError: error => {
      errorToast('Failed to update the table data.', error)
    },
    onSettled: () => {
      successToast('Successfully updated!')
    },
  })

  let statusMessage = null
  if (isError) {
    statusMessage = (
      <div className={classes.statusMessage}>
        Failed to load reports: {error?.message || 'Unknown error'}
      </div>
    )
  } else if (!reports || reports.length === 0) {
    statusMessage = (
      <div className={classes.statusMessage}>No reports found.</div>
    )
  }

  return (
    <div className={classes.tableOuterWrapper}>
      <div className={classes.tableScrollWrapper}>
        <table className={classes.table}>
          <thead>
            <tr>
              {TABLE_HEADERS.map(header => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statusMessage ? (
              <tr>
                <td colSpan={TABLE_HEADERS.length}>{statusMessage}</td>
              </tr>
            ) : (
              reports.map((r, idx) => (
                <ReportRow
                  key={r.f_id}
                  r={r}
                  idx={idx}
                  isLast={idx === reports.length - 1}
                  lastRowRef={lastRowRef}
                  selectedRowId={selectedRowId}
                  setSelectedRowId={setSelectedRowId}
                  handleStatusChange={handleStatusChange}
                  handlePriorityChange={handlePriorityChange}
                  handleDepartmentChange={handleDepartmentChange}
                  handleEditorNotesChange={handleEditorNotesChange}
                  handleAssignedChange={handleAssignedChange}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

ReportsTable.propTypes = {
  reports: PropTypes.array,
  lastRowRef: PropTypes.func,
  isError: PropTypes.bool,
  error: PropTypes.string,
}

export default ReportsTable
